import React, { useEffect } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import PageShell from './PageShell';
import Seo from '../components/Seo';
import { useI18n } from '../context/I18nContext';
import { useUserAuth } from '../context/UserAuthContext';
import { useToast } from '../hooks/use-toast';
import { publicApi } from '../api';
import { useApi } from '../hooks/useApi';
import { staticImg } from '../utils/media';
import { Download, ShieldCheck, Zap, ArrowLeft, CheckCircle2, LogIn } from 'lucide-react';

export default function FreeProductDetail() {
  const { slug } = useParams();
  const { lang, tr } = useI18n();
  const { user } = useUserAuth();
  const { toast } = useToast();
  const nav = useNavigate();
  const { data: p, error } = useApi(() => publicApi.freeProduct(slug, lang), [slug, lang]);
  useEffect(() => { if (p) document.title = `${p.name} — Aegis Studios`; }, [p]);
  if (error) return <Navigate to="/free-products" replace />;
  if (!p) return <PageShell eyebrow="" title="..." />;

  const handleDownload = () => {
    if (!user) {
      toast({ title: 'Giriş yapmalısınız', description: 'Ücretsiz ürünleri indirmek için giriş yapmanız gerekiyor.' });
      nav(`/login?redirect=/free-products/${slug}`);
      return;
    }
    toast({ title: 'İndirme başlıyor...', description: p.name });
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    window.location.href = `${backendUrl}/api/free-products/${p.id}/download`;
  };

  return (
    <PageShell eyebrow="ÜCRETSİZ ÜRÜN" title={<span className="text-white">{p.name}</span>} subtitle={p.short_description}>
      <Seo title={p.meta_title || p.name} description={p.meta_description || p.short_description} image={staticImg(p.image)} />
      <section className="pb-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 lg:px-10">
          <Link to="/free-products" className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#f4d896] mb-8">
            <ArrowLeft className="w-4 h-4" /> {tr('free.back', 'Tüm ücretsiz ürünlere dön')}
          </Link>
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-8 lg:gap-10">
            <div>
              <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-[#0f0e12] aspect-[16/10]">
                <img src={staticImg(p.image)} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
              </div>
              {p.description_html ? (
                <article className="prose prose-invert prose-p:text-white/70 prose-headings:text-white prose-strong:text-white mt-10 max-w-none" dangerouslySetInnerHTML={{ __html: p.description_html }} />
              ) : null}
            </div>
            <div className="rounded-3xl border border-emerald-500/25 bg-gradient-to-b from-emerald-950/40 to-[#0a090c] p-6 md:p-8 h-fit lg:sticky lg:top-24">
              <div className="text-[10px] font-bold tracking-[0.3em] text-emerald-300/85 mb-2">ÜCRETSİZ • METIN2 SİSTEMLERİ</div>
              <h2 className="font-display text-2xl md:text-3xl font-black text-white mb-4">{p.name}</h2>
              <p className="text-white/70 leading-relaxed mb-6">{p.short_description}</p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm text-white/80"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Açık kaynak & ücretsiz</div>
                <div className="flex items-center gap-3 text-sm text-white/80"><Zap className="w-4 h-4 text-[#e4b44e]" /> Anında indirilebilir</div>
                <div className="flex items-center gap-3 text-sm text-white/80"><ShieldCheck className="w-4 h-4 text-[#e4b44e]" /> Aegis Studios güvencesi</div>
              </div>
              <button onClick={handleDownload} className="w-full h-12 rounded-full inline-flex items-center justify-center gap-2 text-sm font-extrabold tracking-widest bg-emerald-500 hover:bg-emerald-400 text-black transition">
                {user ? <><Download className="w-4 h-4" /> ÜCRETSİZ İNDİR</> : <><LogIn className="w-4 h-4" /> GİRİŞ YAPARAK İNDİR</>}
              </button>
              {!user && <div className="mt-3 text-xs text-white/50 text-center">Ücretsiz ürünler yalnızca üyelere açıktır. <Link to="/register" className="text-emerald-400 hover:underline">Kayıt ol</Link></div>}
            </div>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
